import {
  fetchAndActivate,
  getValue,
  RemoteConfig,
} from 'firebase/remote-config';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type RemoteConfigContextValue = {
  remoteConfig: RemoteConfig | null;
};

type RemoteConfigFetchState = {
  inFlight?: Promise<void>;
  lastAttemptAt?: number;
};

const remoteConfigFetchState = new WeakMap<
  RemoteConfig,
  RemoteConfigFetchState
>();
const DEFAULT_MINIMUM_FETCH_INTERVAL_MS = 60000;

const RemoteConfigContext = createContext<RemoteConfigContextValue | undefined>(
  undefined
);

type RemoteConfigProviderProps = {
  remoteConfig: RemoteConfig;
  children: React.ReactNode;
};

export const RemoteConfigProvider = ({
  remoteConfig,
  children,
}: RemoteConfigProviderProps) => {
  const value = useMemo(() => ({ remoteConfig }), [remoteConfig]);

  return (
    <RemoteConfigContext.Provider value={value}>
      {children}
    </RemoteConfigContext.Provider>
  );
};

const getMinimumFetchInterval = (remoteConfig: RemoteConfig) =>
  remoteConfig.settings.minimumFetchIntervalMillis ??
  DEFAULT_MINIMUM_FETCH_INTERVAL_MS;

const fetchRemoteConfig = (remoteConfig: RemoteConfig) => {
  const state = remoteConfigFetchState.get(remoteConfig) ?? {};
  const now = Date.now();

  if (state.inFlight) {
    return state.inFlight;
  }

  if (
    state.lastAttemptAt !== undefined &&
    now - state.lastAttemptAt < getMinimumFetchInterval(remoteConfig)
  ) {
    return Promise.resolve();
  }

  const inFlight = fetchAndActivate(remoteConfig)
    .then(() => undefined)
    .finally(() => {
      const currentState = remoteConfigFetchState.get(remoteConfig);
      if (currentState) {
        currentState.inFlight = undefined;
      }
    });

  remoteConfigFetchState.set(remoteConfig, {
    ...state,
    inFlight,
    lastAttemptAt: now,
  });

  return inFlight;
};

const useRemoteConfig = (key: string) => {
  const context = useContext(RemoteConfigContext);
  const [value, setValue] = useState<any>();

  useEffect(() => {
    if (!context?.remoteConfig) {
      setValue(undefined);
      return;
    }

    let isActive = true;

    const readValue = () => {
      const configValue = getValue(context.remoteConfig, key);
      const rawValue = configValue.asString();
      if (!rawValue) {
        setValue(undefined);
        return;
      }

      try {
        setValue(JSON.parse(rawValue));
      } catch (error) {
        console.error('Failed to parse Remote Config JSON', error);
        setValue(configValue.asBoolean());
      }
    };

    const fetchConfig = async () => {
      try {
        readValue();
        if (!navigator.onLine) return;
        await fetchRemoteConfig(context.remoteConfig);
        if (isActive) {
          readValue();
        }
      } catch (error) {
        console.error('Failed to fetch Remote Config', error);
        if (isActive) {
          setValue(false);
        }
      }
    };

    fetchConfig();
    window.addEventListener('online', fetchConfig);

    return () => {
      isActive = false;
      window.removeEventListener('online', fetchConfig);
    };
  }, [context?.remoteConfig, key]);

  return value;
};

export default useRemoteConfig;
